import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import { sha256 } from '@noble/hashes/sha256'
import type { IdentityProvider, UserIdentity } from './types'
import {
    assertKeyDerivations,
    createCanonicalIdentity,
    decodeBase64Url,
    encodeBase64Url,
    type CanonicalIdentity,
    normalizeCanonicalIdentity,
    validateCanonicalIdentity,
} from './identity-core'
import {
    decryptAesGcm,
    encryptAesGcm,
    generateVaultKeyMaterial,
    importVaultKey,
    randomIv,
    unwrapVaultKeyWithPin,
    unwrapVaultKeyWithPrf,
    wrapVaultKeyWithPin,
    wrapVaultKeyWithPrf,
    zeroize,
} from './vault-crypto'

/**
 * Coerce any Uint8Array to one backed by a standalone ArrayBuffer.
 * See vault-crypto.ts for rationale (TS 5.7+ generic Uint8Array).
 */
function toAB(src: Uint8Array): Uint8Array<ArrayBuffer> {
    if (src.buffer instanceof ArrayBuffer && src.byteOffset === 0 && src.byteLength === src.buffer.byteLength) {
        return src as Uint8Array<ArrayBuffer>
    }
    return new Uint8Array(src) as Uint8Array<ArrayBuffer>
}

const IDENTITY_AAD = new TextEncoder().encode('secureblob-identity')
const AUTO_LOCK_DEFAULT_MS = 14 * 24 * 60 * 60 * 1000
const IV_BYTES = 12
const SESSION_KEY_STORAGE_KEY = 'sb:vault-session-key'
const BROADCAST_CHANNEL_NAME = 'sb:vault-unlock'
const CROSS_TAB_TIMEOUT_MS = 500

interface IdentityVaultRecord {
    vaultKeyCiphertext: ArrayBuffer
    identityCiphertext: ArrayBuffer
    keyWrapSalt: ArrayBuffer
    keyWrapIv: ArrayBuffer
    identityIv: ArrayBuffer
    credentialId?: ArrayBuffer
    prfSalt?: ArrayBuffer
    unlockMethod?: VaultUnlockMethod
    createdAt: string
    updatedAt: string
}

export interface IdentityVaultStore {
    read(): Promise<IdentityVaultRecord | null>
    write(record: IdentityVaultRecord): Promise<void>
    clear(): Promise<void>
    /** Check if a vault record exists without reading full data. */
    hasRecord(): Promise<boolean>
}

export class MemoryIdentityVaultStore implements IdentityVaultStore {
    private record: IdentityVaultRecord | null = null

    async read(): Promise<IdentityVaultRecord | null> {
        return this.record
    }

    async write(record: IdentityVaultRecord): Promise<void> {
        this.record = record
    }

    async clear(): Promise<void> {
        this.record = null
    }

    async hasRecord(): Promise<boolean> {
        return this.record !== null
    }
}

interface IdentityVaultDb extends DBSchema {
    identity_vault: {
        key: string
        value: { id: 'default'; record: IdentityVaultRecord }
    }
}

export class IdbIdentityVaultStore implements IdentityVaultStore {
    private dbPromise: Promise<IDBPDatabase<IdentityVaultDb>>

    constructor(dbName = 'secureblob-identity-vault-v1') {
        this.dbPromise = openDB<IdentityVaultDb>(dbName, 1, {
            upgrade(db) {
                db.createObjectStore('identity_vault', { keyPath: 'id' })
            },
        })
    }

    async read(): Promise<IdentityVaultRecord | null> {
        const db = await this.dbPromise
        const row = await db.get('identity_vault', 'default')
        return row?.record ?? null
    }

    async write(record: IdentityVaultRecord): Promise<void> {
        const db = await this.dbPromise
        await db.put('identity_vault', { id: 'default', record })
    }

    async clear(): Promise<void> {
        const db = await this.dbPromise
        await db.delete('identity_vault', 'default')
    }

    async hasRecord(): Promise<boolean> {
        const db = await this.dbPromise
        const row = await db.get('identity_vault', 'default')
        return row != null
    }
}
// ─── Trusted Device types (§05 §10) ─────────────────────────────────────────

/**
 * What the server stores per trusted device (§05 §10.2).
 * The server NEVER has access to K_vault plaintext — only ciphertext.
 * Internal use — ArrayBuffer fields for crypto operations.
 */
export interface TrustedDeviceEnvelope {
    deviceId: string
    vaultEnvelopeCiphertext: ArrayBuffer   // AES-GCM(K_trustedDeviceWrapKey, K_vault)
    keyWrapSalt: ArrayBuffer
    keyWrapIv: ArrayBuffer
    devicePublicKey: string               // base64url(32-byte Ed25519 public key)
    label?: string
    createdAt: string
    lastSeenAt: string
    revokedAt?: string
}

/**
 * API response for trusted device envelope — uses base64url strings over the wire.
 */
export interface TrustedDeviceEnvelopeResponse {
    deviceId: string
    vaultEnvelopeCiphertext: string   // base64url
    keyWrapSalt: string               // base64url
    keyWrapIv: string                // base64url
    devicePublicKey: string          // base64url
    label?: string
    createdAt: string
    lastSeenAt: string
    revokedAt?: string
}

/**
 * What the browser stores locally per trusted device (§05 §10.2).
 * deviceAuthPrivateKey is non-extractable — never leaves the browser.
 */
export interface TrustedDeviceLocalRecord {
    deviceId: string
    deviceAuthPrivateKey: CryptoKey       // Ed25519 private key — non-extractable
    deviceLocalSecret: Uint8Array        // 32-byte random — never sent to server
    primaryKid: string                   // primary key ID used at registration
    createdAt: string
}

/**
 * Device info returned from the server list endpoint.
 * Does NOT include secrets.
 */
export interface TrustedDeviceInfo {
    deviceId: string
    label?: string
    createdAt: string
    lastSeenAt: string
    revokedAt?: string
}

// ─── Trusted Device Store ───────────────────────────────────────────────────

interface TrustedDeviceDb extends DBSchema {
    trusted_device: {
        key: string
        value: { id: 'current'; record: TrustedDeviceLocalRecord }
    }
}

export interface TrustedDeviceStore {
    read(): Promise<TrustedDeviceLocalRecord | null>
    write(record: TrustedDeviceLocalRecord): Promise<void>
    clear(): Promise<void>
}

export class IdbTrustedDeviceStore implements TrustedDeviceStore {
    private dbPromise: Promise<IDBPDatabase<TrustedDeviceDb>>

    constructor(dbName = 'secureblob-trusted-device-v1') {
        this.dbPromise = openDB<TrustedDeviceDb>(dbName, 1, {
            upgrade(db) {
                db.createObjectStore('trusted_device', { keyPath: 'id' })
            },
        })
    }

    async read(): Promise<TrustedDeviceLocalRecord | null> {
        const db = await this.dbPromise
        const row = await db.get('trusted_device', 'current')
        return row?.record ?? null
    }

    async write(record: TrustedDeviceLocalRecord): Promise<void> {
        const db = await this.dbPromise
        await db.put('trusted_device', { id: 'current', record })
    }

    async clear(): Promise<void> {
        const db = await this.dbPromise
        await db.delete('trusted_device', 'current')
    }
}

export class MemoryTrustedDeviceStore implements TrustedDeviceStore {
    private record: TrustedDeviceLocalRecord | null = null

    async read(): Promise<TrustedDeviceLocalRecord | null> {
        return this.record
    }

    async write(record: TrustedDeviceLocalRecord): Promise<void> {
        this.record = record
    }

    async clear(): Promise<void> {
        this.record = null
    }
}

type VaultUnlockMethod = 'pin' | 'webauthn' | 'webauthn-prf'

interface StoredIdentityPayload {
    canonical: CanonicalIdentity
    profile?: {
        name?: string
        emoji?: string
    }
}

interface SerializedIdentityVaultRecord {
    vaultKeyCiphertext: string
    identityCiphertext: string
    keyWrapSalt: string
    keyWrapIv: string
    identityIv: string
    credentialId?: string
    prfSalt?: string
    unlockMethod?: VaultUnlockMethod
    createdAt: string
    updatedAt: string
}

function encodeBuffer(buffer: ArrayBuffer): string {
    return encodeBase64Url(new Uint8Array(buffer))
}

function decodeBuffer(encoded: string): ArrayBuffer {
    return toAB(decodeBase64Url(encoded)).buffer as ArrayBuffer
}

function serializeIdentityVaultRecord(record: IdentityVaultRecord): SerializedIdentityVaultRecord {
    return {
        vaultKeyCiphertext: encodeBuffer(record.vaultKeyCiphertext),
        identityCiphertext: encodeBuffer(record.identityCiphertext),
        keyWrapSalt: encodeBuffer(record.keyWrapSalt),
        keyWrapIv: encodeBuffer(record.keyWrapIv),
        identityIv: encodeBuffer(record.identityIv),
        credentialId: record.credentialId ? encodeBuffer(record.credentialId) : undefined,
        prfSalt: record.prfSalt ? encodeBuffer(record.prfSalt) : undefined,
        unlockMethod: record.unlockMethod,
        createdAt: record.createdAt,
        updatedAt: record.updatedAt,
    }
}

function deserializeIdentityVaultRecord(record: SerializedIdentityVaultRecord): IdentityVaultRecord {
    return {
        vaultKeyCiphertext: decodeBuffer(record.vaultKeyCiphertext),
        identityCiphertext: decodeBuffer(record.identityCiphertext),
        keyWrapSalt: decodeBuffer(record.keyWrapSalt),
        keyWrapIv: decodeBuffer(record.keyWrapIv),
        identityIv: decodeBuffer(record.identityIv),
        credentialId: record.credentialId ? decodeBuffer(record.credentialId) : undefined,
        prfSalt: record.prfSalt ? decodeBuffer(record.prfSalt) : undefined,
        unlockMethod: record.unlockMethod,
        createdAt: record.createdAt,
        updatedAt: record.updatedAt,
    }
}

function isSerializedIdentityVaultRecord(record: unknown): record is SerializedIdentityVaultRecord {
    if (!record || typeof record !== 'object') return false
    const candidate = record as Partial<SerializedIdentityVaultRecord>
    return typeof candidate.vaultKeyCiphertext === 'string'
        && typeof candidate.identityCiphertext === 'string'
        && typeof candidate.keyWrapSalt === 'string'
        && typeof candidate.keyWrapIv === 'string'
        && typeof candidate.identityIv === 'string'
        && typeof candidate.createdAt === 'string'
        && typeof candidate.updatedAt === 'string'
}

function toCompatIdentity(payload: StoredIdentityPayload): UserIdentity {
    const primary = payload.canonical.keyRing.find((entry) => entry.source === 'primary')
    if (!primary) throw new Error('Canonical identity missing primary key')

    return {
        kid: primary.kid,
        name: payload.profile?.name ?? 'Anonymous',
        emoji: payload.profile?.emoji ?? '🔑',
        keyRing: payload.canonical.keyRing,
        canonical: payload.canonical,
    }
}

async function fromCompatIdentity(identity: UserIdentity): Promise<StoredIdentityPayload> {
    const canonical = identity.canonical
        ? await normalizeCanonicalIdentity(identity.canonical as any)
        : await createCanonicalIdentity()
    return {
        canonical,
        profile: { name: identity.name, emoji: identity.emoji },
    }
}

// Internal helpers for WebAuthn (since vault-crypto doesn't have WebAuthn variants yet)
async function deriveWebAuthnWrapKey(credentialId: Uint8Array, salt: Uint8Array): Promise<CryptoKey> {
    const material = await crypto.subtle.importKey('raw', toAB(credentialId), 'PBKDF2', false, ['deriveKey'])
    return crypto.subtle.deriveKey(
        { name: 'PBKDF2', hash: 'SHA-256', salt: toAB(salt), iterations: 210_000 },
        material,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt'],
    )
}

// §10.1 WebAuthn support
export interface WebAuthnAssertionResult {
    ok: boolean
    prfOutput?: Uint8Array  // PRF first output, if extension was requested and supported
}

export interface WebAuthnCreateResult {
    credentialId: Uint8Array
    prfOutput?: Uint8Array  // PRF first output from registration, if supported
    prfSalt?: Uint8Array    // The PRF salt used during registration (must be stored for unlock)
}

export interface WebAuthnAdapter {
    createCredential(displayName?: string): Promise<WebAuthnCreateResult | null>
    getAssertion(credentialId: Uint8Array, signal?: AbortSignal, prfSalt?: Uint8Array): Promise<WebAuthnAssertionResult>
    getDiscoverableAssertion(signal?: AbortSignal, prfSalt?: Uint8Array): Promise<{ credentialId: Uint8Array; prfOutput?: Uint8Array } | null>
}

const WEBAUTHN_TIMEOUT_MS = 15_000

/**
 * Resolve the WebAuthn Relying Party ID.
 *
 * Production: 'hoglundit.se' — scoped to the registrable domain so passkeys
 * created on secureblob.hoglundit.se sync via iCloud Keychain and work across
 * all *.hoglundit.se subdomains.
 *
 * Dev/localhost: undefined — lets the browser default to the current hostname
 * (e.g. 'localhost'), avoiding rpId mismatch errors during local development.
 */
function getWebAuthnRpId(): string | undefined {
    if (typeof window === 'undefined') return undefined
    const host = window.location.hostname
    if (host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0') return undefined
    return 'secureblob.hoglundit.se'
}

class BrowserWebAuthnAdapter implements WebAuthnAdapter {
    async createCredential(displayName?: string): Promise<WebAuthnCreateResult | null> {
        if (typeof window === 'undefined' || !window.PublicKeyCredential || !navigator.credentials) return null
        const challenge = crypto.getRandomValues(new Uint8Array(32))
        const userId = crypto.getRandomValues(new Uint8Array(32))
        const userName = displayName || 'secureblob-user'
        const ac = typeof AbortController !== 'undefined' ? new AbortController() : undefined
        const timer = ac ? setTimeout(() => ac.abort(), WEBAUTHN_TIMEOUT_MS + 2_000) : undefined

        // PRF extension salt for registration
        const prfSalt = crypto.getRandomValues(new Uint8Array(32))

        try {
            const rpId = getWebAuthnRpId()
            const credential = await navigator.credentials.create({
                publicKey: {
                    challenge,
                    rp: { name: 'SecureBlob', ...(rpId ? { id: rpId } : {}) },
                    user: { id: userId, name: userName, displayName: userName },
                    pubKeyCredParams: [
                        { type: 'public-key', alg: -7 },   // ES256 — Apple, Android, modern authenticators
                        { type: 'public-key', alg: -257 },  // RS256 — Windows Hello, older authenticators
                    ],
                    authenticatorSelection: {
                        // Do NOT set authenticatorAttachment: 'platform' here!
                        // It blocks third-party passkey managers (1Password, Bitwarden) on Windows.
                        // Platform preference is achieved via hints: ['client-device'] on get() instead.
                        userVerification: 'required',
                        residentKey: 'required',
                    },
                    timeout: WEBAUTHN_TIMEOUT_MS,
                    extensions: {
                        prf: { eval: { first: prfSalt } },
                    },
                },
                signal: ac?.signal,
            }) as PublicKeyCredential | null

            if (!credential) return null

            // Check if PRF extension was supported
            const ext = (credential.getClientExtensionResults?.() ?? {}) as { prf?: { enabled?: boolean; results?: { first?: ArrayBuffer } } }
            let prfOutput: Uint8Array | undefined
            if (ext.prf?.results?.first) {
                prfOutput = new Uint8Array(ext.prf.results.first)
            }

            return { credentialId: new Uint8Array(credential.rawId), prfOutput, prfSalt }
        } catch (e) {
            console.warn('WebAuthn registration failed:', e)
            return null
        } finally {
            if (timer) clearTimeout(timer)
        }
    }

    async getAssertion(credentialId: Uint8Array, externalSignal?: AbortSignal, prfSalt?: Uint8Array): Promise<WebAuthnAssertionResult> {
        if (typeof window === 'undefined' || !window.PublicKeyCredential || !navigator.credentials) return { ok: false }
        const challenge = crypto.getRandomValues(new Uint8Array(32))
        // Use external signal if provided (e.g. for silent unlock), otherwise create our own
        const useExternalSignal = !!externalSignal
        const ac = !useExternalSignal && typeof AbortController !== 'undefined' ? new AbortController() : undefined
        const timer = ac ? setTimeout(() => ac.abort(), WEBAUTHN_TIMEOUT_MS + 2_000) : undefined
        const signal = externalSignal ?? ac?.signal

        const extensions: Record<string, unknown> = {}
        if (prfSalt) {
            extensions.prf = { eval: { first: prfSalt } }
        }

        try {
            const rpId = getWebAuthnRpId()
            const assertion = await navigator.credentials.get({
                publicKey: {
                    challenge,
                    ...(rpId ? { rpId } : {}),
                    allowCredentials: [{ type: 'public-key', id: toAB(credentialId) }],
                    userVerification: 'required',
                    timeout: useExternalSignal ? 3_000 : WEBAUTHN_TIMEOUT_MS,
                    // WebAuthn L3: prefer platform authenticator (iCloud Keychain / Touch ID / Face ID)
                    hints: ['client-device'],
                    extensions: Object.keys(extensions).length > 0 ? extensions : undefined,
                } as PublicKeyCredentialRequestOptions,
                signal,
            }) as PublicKeyCredential | null

            if (!assertion) return { ok: false }

            // Extract PRF output if available
            const ext = (assertion.getClientExtensionResults?.() ?? {}) as { prf?: { results?: { first?: ArrayBuffer } } }
            let prfOutput: Uint8Array | undefined
            if (ext.prf?.results?.first) {
                prfOutput = new Uint8Array(ext.prf.results.first)
            }

            return { ok: true, prfOutput }
        } catch (e) {
            console.warn('WebAuthn assertion failed:', e)
            return { ok: false }
        } finally {
            if (timer) clearTimeout(timer)
        }
    }

    async getDiscoverableAssertion(externalSignal?: AbortSignal, prfSalt?: Uint8Array): Promise<{ credentialId: Uint8Array; prfOutput?: Uint8Array } | null> {
        if (typeof window === 'undefined' || !window.PublicKeyCredential || !navigator.credentials) return null
        const challenge = crypto.getRandomValues(new Uint8Array(32))
        const ac = !externalSignal && typeof AbortController !== 'undefined' ? new AbortController() : undefined
        const timer = ac ? setTimeout(() => ac.abort(), WEBAUTHN_TIMEOUT_MS + 2_000) : undefined
        const signal = externalSignal ?? ac?.signal

        const extensions: Record<string, unknown> = {}
        if (prfSalt) {
            extensions.prf = { eval: { first: prfSalt } }
        }

        try {
            const rpId = getWebAuthnRpId()
            const assertion = await navigator.credentials.get({
                publicKey: {
                    challenge,
                    // No allowCredentials -> browser shows passkey picker (discoverable)
                    ...(rpId ? { rpId } : {}),
                    userVerification: 'required',
                    timeout: WEBAUTHN_TIMEOUT_MS,
                    // WebAuthn L3: prefer platform authenticator (iCloud Keychain / Touch ID / Face ID)
                    // This prevents Safari/iOS from showing QR/USB options before the synced passkey.
                    hints: ['client-device'],
                    extensions: Object.keys(extensions).length > 0 ? extensions : undefined,
                } as PublicKeyCredentialRequestOptions,
                signal,
            }) as PublicKeyCredential | null

            if (!assertion) return null

            const ext = (assertion.getClientExtensionResults?.() ?? {}) as { prf?: { results?: { first?: ArrayBuffer } } }
            let prfOutput: Uint8Array | undefined
            if (ext.prf?.results?.first) {
                prfOutput = new Uint8Array(ext.prf.results.first)
            }

            return { credentialId: new Uint8Array(assertion.rawId), prfOutput }
        } catch (e) {
            console.warn('WebAuthn discoverable assertion failed:', e)
            return null
        } finally {
            if (timer) clearTimeout(timer)
        }
    }
}

/**
 * Save identity to vault. Returns the raw vault key bytes for session caching.
 * The caller MUST zeroize the returned key after caching.
 */
export async function saveIdentityToVault(
    store: IdentityVaultStore,
    identity: StoredIdentityPayload,
    opts: { pin?: string; credentialId?: Uint8Array; prfOutput?: Uint8Array; prfSalt?: Uint8Array },
): Promise<Uint8Array> {
    const now = new Date().toISOString()
    validateCanonicalIdentity(identity.canonical)
    await assertKeyDerivations(identity.canonical)

    const vaultMaterial = await generateVaultKeyMaterial()
    // Copy before zeroize so caller can cache it
    const rawKeyCopy = new Uint8Array(vaultMaterial.raw)

    let wrapped: { vaultKeyCiphertext: ArrayBuffer; keyWrapSalt: ArrayBuffer; keyWrapIv: ArrayBuffer }
    let unlockMethod: 'pin' | 'webauthn' | 'webauthn-prf' = 'pin'
    let prfSalt: ArrayBuffer | undefined

    if (opts.credentialId && opts.prfOutput && opts.prfSalt) {
        // PRF path: hardware-bound key wrapping (most secure)
        // Requires the SAME salt used during registration so unlock produces the same PRF output
        wrapped = await wrapVaultKeyWithPrf(vaultMaterial.raw, opts.prfOutput)
        prfSalt = opts.prfSalt.buffer as ArrayBuffer
        unlockMethod = 'webauthn-prf'
    } else if (opts.credentialId) {
        // Legacy WebAuthn path: PBKDF2(credentialId) — fallback when PRF unavailable
        const salt = crypto.getRandomValues(new Uint8Array(16))
        const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES))
        const wrapKey = await deriveWebAuthnWrapKey(opts.credentialId, salt)
        const vaultKeyCiphertext = await encryptAesGcm(wrapKey, iv, vaultMaterial.raw)
        wrapped = { vaultKeyCiphertext, keyWrapSalt: salt.buffer as ArrayBuffer, keyWrapIv: iv.buffer as ArrayBuffer }
        unlockMethod = 'webauthn'
    } else {
        const pin = opts.pin ?? '0000'
        wrapped = await wrapVaultKeyWithPin(vaultMaterial.raw, pin)
    }

    const identityIv = randomIv()
    const identityBytes = new TextEncoder().encode(JSON.stringify(identity))
    const identityCiphertext = await encryptAesGcm(vaultMaterial.key, identityIv, identityBytes, IDENTITY_AAD)

    await store.write({
        ...wrapped,
        identityIv: identityIv.buffer as ArrayBuffer,
        identityCiphertext,
        credentialId: opts.credentialId ? (opts.credentialId.buffer as ArrayBuffer) : undefined,
        prfSalt,
        unlockMethod,
        createdAt: now,
        updatedAt: now,
    })

    zeroize(identityBytes)
    zeroize(vaultMaterial.raw)
    return rawKeyCopy
}

export async function loadIdentityFromVault(store: IdentityVaultStore, pinOrCredential?: string | Uint8Array): Promise<StoredIdentityPayload | null> {
    const record = await store.read()
    if (!record) return null

    let rawVaultKey: Uint8Array
    if (record.unlockMethod === 'webauthn') {
        if (!(pinOrCredential instanceof Uint8Array)) throw new Error('WebAuthn credential required for unlock')
        const wrapKey = await deriveWebAuthnWrapKey(pinOrCredential, new Uint8Array(record.keyWrapSalt))
        const decrypted = await decryptAesGcm(wrapKey, new Uint8Array(record.keyWrapIv), record.vaultKeyCiphertext)
        rawVaultKey = new Uint8Array(decrypted)
    } else if (record.unlockMethod === 'webauthn-prf') {
        // PRF vaults cannot be loaded via this function — use loadIdentityWithPrf()
        throw new Error('PRF vault requires assertion with PRF extension output')
    } else {
        const pin = typeof pinOrCredential === 'string' ? pinOrCredential : '0000'
        rawVaultKey = await unwrapVaultKeyWithPin({
            vaultKeyCiphertext: record.vaultKeyCiphertext,
            keyWrapSalt: record.keyWrapSalt,
            keyWrapIv: record.keyWrapIv,
        }, pin)
    }

    const vaultKey = await importVaultKey(rawVaultKey)
    const identityBytes = await decryptAesGcm(vaultKey, new Uint8Array(record.identityIv), record.identityCiphertext, IDENTITY_AAD)
    const parsed = JSON.parse(new TextDecoder().decode(identityBytes)) as StoredIdentityPayload

    parsed.canonical = await normalizeCanonicalIdentity(parsed.canonical as any)
    validateCanonicalIdentity(parsed.canonical)
    await assertKeyDerivations(parsed.canonical)

    zeroize(rawVaultKey)
    return parsed
}

/**
 * Load identity from a PRF-protected vault using the PRF assertion output.
 * Returns the raw vault key along with the identity for session caching.
 */
export async function loadIdentityWithPrf(
    store: IdentityVaultStore,
    prfOutput: Uint8Array,
): Promise<{ payload: StoredIdentityPayload; rawKey: Uint8Array } | null> {
    const record = await store.read()
    if (!record || record.unlockMethod !== 'webauthn-prf') return null

    const rawVaultKey = await unwrapVaultKeyWithPrf({
        vaultKeyCiphertext: record.vaultKeyCiphertext,
        keyWrapSalt: record.keyWrapSalt,
        keyWrapIv: record.keyWrapIv,
    }, prfOutput)

    const vaultKey = await importVaultKey(rawVaultKey)
    const identityBytes = await decryptAesGcm(vaultKey, new Uint8Array(record.identityIv), record.identityCiphertext, IDENTITY_AAD)
    const parsed = JSON.parse(new TextDecoder().decode(identityBytes)) as StoredIdentityPayload

    parsed.canonical = await normalizeCanonicalIdentity(parsed.canonical as any)
    validateCanonicalIdentity(parsed.canonical)
    await assertKeyDerivations(parsed.canonical)

    return { payload: parsed, rawKey: rawVaultKey }
}

/**
 * Load identity using raw vault key bytes directly (bypasses PIN/credential derivation).
 * Used by session cache to avoid PBKDF2/WebAuthn on reload.
 */
export async function loadIdentityWithRawKey(store: IdentityVaultStore, rawKey: Uint8Array): Promise<StoredIdentityPayload | null> {
    const record = await store.read()
    if (!record) return null

    const vaultKey = await importVaultKey(rawKey)
    const identityBytes = await decryptAesGcm(vaultKey, new Uint8Array(record.identityIv), record.identityCiphertext, IDENTITY_AAD)
    const parsed = JSON.parse(new TextDecoder().decode(identityBytes)) as StoredIdentityPayload

    parsed.canonical = await normalizeCanonicalIdentity(parsed.canonical as any)
    validateCanonicalIdentity(parsed.canonical)
    await assertKeyDerivations(parsed.canonical)

    return parsed
}

async function saveIdentityWithRawKey(
    store: IdentityVaultStore,
    identity: StoredIdentityPayload,
    rawKey: Uint8Array,
): Promise<void> {
    const record = await store.read()
    if (!record) throw new Error('No vault record found')

    validateCanonicalIdentity(identity.canonical)
    await assertKeyDerivations(identity.canonical)

    const identityIv = randomIv()
    const identityBytes = new TextEncoder().encode(JSON.stringify(identity))
    const vaultKey = await importVaultKey(rawKey)
    const identityCiphertext = await encryptAesGcm(vaultKey, identityIv, identityBytes, IDENTITY_AAD)

    await store.write({
        ...record,
        identityIv: identityIv.buffer as ArrayBuffer,
        identityCiphertext,
        updatedAt: new Date().toISOString(),
    })

    zeroize(identityBytes)
}

// ─── Trusted Device helpers (§05 §10) ──────────────────────────────────────

const TRUSTED_DEVICE_HKDF_INFO = new TextEncoder().encode('secureblob:trusted-device-unlock')

/**
 * Derive the trusted-device wrap key per §05 §10.3:
 * trustedDeviceWrapKey = HKDF-SHA256(ikm = deviceLocalSecret || serverGrantSecret, salt = primaryKid, info)
 */
async function deriveTrustedDeviceWrapKey(
    deviceLocalSecret: Uint8Array,
    serverGrantSecret: Uint8Array,
    primaryKid: string,
): Promise<CryptoKey> {
    const ikm = new Uint8Array(deviceLocalSecret.length + serverGrantSecret.length)
    ikm.set(deviceLocalSecret, 0)
    ikm.set(serverGrantSecret, deviceLocalSecret.length)

    const salt = new TextEncoder().encode(primaryKid)
    const material = await crypto.subtle.importKey('raw', toAB(ikm), 'HKDF', false, ['deriveKey'])
    zeroize(ikm)

    return crypto.subtle.deriveKey(
        { name: 'HKDF', hash: 'SHA-256', salt, info: toAB(TRUSTED_DEVICE_HKDF_INFO) },
        material,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt'],
    )
}

/**
 * Wrap K_vault for trusted-device storage using the derived wrap key.
 */
async function wrapVaultKeyWithTrustedDeviceKey(
    rawVaultKey: Uint8Array,
    deviceLocalSecret: Uint8Array,
    serverGrantSecret: Uint8Array,
    primaryKid: string,
): Promise<{ vaultEnvelopeCiphertext: ArrayBuffer; keyWrapSalt: ArrayBuffer; keyWrapIv: ArrayBuffer }> {
    const wrapKey = await deriveTrustedDeviceWrapKey(deviceLocalSecret, serverGrantSecret, primaryKid)
    const wrapSalt = crypto.getRandomValues(new Uint8Array(16))
    const wrapIv = randomIv()
    const ciphertext = await encryptAesGcm(wrapKey, wrapIv, rawVaultKey)
    return {
        vaultEnvelopeCiphertext: ciphertext,
        keyWrapSalt: wrapSalt.buffer as ArrayBuffer,
        keyWrapIv: wrapIv.buffer as ArrayBuffer,
    }
}

/**
 * Unwrap K_vault from trusted-device envelope using local device secret + server grant.
 */
async function unwrapVaultKeyWithTrustedDeviceKey(
    envelope: TrustedDeviceEnvelope,
    deviceLocalSecret: Uint8Array,
    serverGrantSecret: Uint8Array,
    primaryKid: string,
): Promise<Uint8Array> {
    const wrapKey = await deriveTrustedDeviceWrapKey(deviceLocalSecret, serverGrantSecret, primaryKid)
    const rawKey = await decryptAesGcm(
        wrapKey,
        new Uint8Array(envelope.keyWrapIv),
        envelope.vaultEnvelopeCiphertext,
    )
    return new Uint8Array(rawKey)
}

/**
 * Generate a new device auth key pair (Ed25519) for trusted-device authentication.
 * Returns { privateKey, publicKeyBase64 }.
 */
async function generateDeviceAuthKeyPair(): Promise<{ privateKey: CryptoKey; publicKeyBase64: string }> {
    const keyPair = await crypto.subtle.generateKey(
        { name: 'Ed25519' } as any,
        false, // extractable = false
        ['sign', 'verify'],
    ) as CryptoKeyPair
    const publicKey = await crypto.subtle.exportKey('raw', keyPair.publicKey)
    return {
        privateKey: keyPair.privateKey,
        publicKeyBase64: encodeBase64Url(new Uint8Array(publicKey)),
    }
}

/**
 * Sign a message with the device auth private key (Ed25519).
 * Uses WebCrypto sign() — the key is non-extractable so we can't use noble/ed25519 directly.
 */
async function signWithDeviceKey(privateKey: CryptoKey, message: string): Promise<string> {
    const msgBytes = new TextEncoder().encode(message)
    const msgHash = sha256(msgBytes)
    const sig = await crypto.subtle.sign(
        { name: 'Ed25519' } as any,
        privateKey,
        toAB(msgHash) as Uint8Array<ArrayBuffer>,
    )
    return encodeBase64Url(new Uint8Array(sig))
}

import { VaultBackupManager } from './vaultBackup'
import { RemoteStore } from './types'

// ─── Random username generator ───────────────────────────────────────────
const ADJECTIVES = [
    'swift', 'quiet', 'bold', 'fuzzy', 'tiny', 'cold', 'warm', 'wild',
    'calm', 'dark', 'pale', 'keen', 'lazy', 'cute', 'sly', 'raw',
]
const NOUNS = [
    'fox', 'owl', 'frog', 'lynx', 'puma', 'hawk', 'wolf', 'bear',
    'crab', 'moth', 'crow', 'dove', 'seal', 'mole', 'hare', 'pike',
]

function generateRandomUsername(): string {
    const adj = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)]
    const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)]
    const num = Math.floor(Math.random() * 100)
    return `${adj}${noun}${num}`
}

export class VaultIdentityProvider implements IdentityProvider {
    private unlocked = false
    private identity: UserIdentity | null = null
    private pinInMemory: string | null = null
    private lockTimer: ReturnType<typeof setTimeout> | null = null
    private backupManager: VaultBackupManager | null = null
    private vaultExistsCache = false
    private vaultUnlockMethodCache: VaultUnlockMethod | null = null
    private vaultStateRefreshVersion = 0

    // ─── Trusted Device state (§05 §10) ──────────────────────────────────
    private trustedDeviceStore: TrustedDeviceStore = new MemoryTrustedDeviceStore()
    private trustedDeviceApiBase: string = '/api/identity/trusted-devices'
    /** In-memory server grant secret — cleared on lock. Never persisted. */
    private serverGrantSecret: Uint8Array | null = null

    constructor(
        private readonly store: IdentityVaultStore,
        private readonly opts: {
            autoLockMs?: number
            webAuthn?: WebAuthnAdapter
            onAutoLock?: () => void
            sessionStorage?: Storage | null
            remoteStore?: RemoteStore
            backupApiBaseUrl?: string
            trustedDeviceStore?: TrustedDeviceStore
            trustedDeviceApiBase?: string
        } = {},
    ) {
        if (opts.remoteStore) {
            this.backupManager = new VaultBackupManager(opts.remoteStore, opts.backupApiBaseUrl)
        }
        if (opts.trustedDeviceStore) {
            this.trustedDeviceStore = opts.trustedDeviceStore
        }
        if (opts.trustedDeviceApiBase) {
            this.trustedDeviceApiBase = opts.trustedDeviceApiBase
        }
        void this.refreshCachedVaultState()
    }

    // ─── Session key cache (§4.6.1) ──────────────────────────────────────
    private get _sessionStore(): Storage | null {
        if (this.opts.sessionStorage !== undefined) return this.opts.sessionStorage
        return typeof sessionStorage !== 'undefined' ? sessionStorage : null
    }

    private cacheSessionKey(rawKey: Uint8Array): void {
        try { this._sessionStore?.setItem(SESSION_KEY_STORAGE_KEY, encodeBase64Url(rawKey)) } catch { /* quota/privacy */ }
    }

    private getCachedSessionKey(): Uint8Array | null {
        try {
            const encoded = this._sessionStore?.getItem(SESSION_KEY_STORAGE_KEY)
            if (!encoded) return null
            const raw = decodeBase64Url(encoded)
            return raw.length === 32 ? raw : null
        } catch { return null }
    }

    private clearSessionKeyCache(): void {
        try { this._sessionStore?.removeItem(SESSION_KEY_STORAGE_KEY) } catch { /* ok */ }
    }

    // ─── Cross-tab unlock (§4.6.2) ───────────────────────────────────────
    private channel: BroadcastChannel | null = null
    private cachedRawKey: Uint8Array | null = null

    /** Start listening for cross-tab key requests. Called after successful unlock. */
    private startBroadcastProvider(): void {
        if (typeof BroadcastChannel === 'undefined') return
        this.stopBroadcast()
        this.channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME)
        this.channel.onmessage = (e: MessageEvent) => {
            if (e.data?.type === 'need-key' && this.unlocked && this.cachedRawKey) {
                this.channel?.postMessage({ type: 'vault-key', raw: encodeBase64Url(this.cachedRawKey) })
            }
        }
    }

    /** Request key from another tab. Returns raw key bytes or null on timeout. */
    private requestKeyFromOtherTab(): Promise<Uint8Array | null> {
        if (typeof BroadcastChannel === 'undefined') return Promise.resolve(null)
        return new Promise((resolve) => {
            const ch = new BroadcastChannel(BROADCAST_CHANNEL_NAME)
            const timer = setTimeout(() => { ch.close(); resolve(null) }, CROSS_TAB_TIMEOUT_MS)
            ch.onmessage = (e: MessageEvent) => {
                if (e.data?.type === 'vault-key' && typeof e.data.raw === 'string') {
                    clearTimeout(timer)
                    ch.close()
                    try {
                        const raw = decodeBase64Url(e.data.raw)
                        resolve(raw.length === 32 ? raw : null)
                    } catch { resolve(null) }
                }
            }
            ch.postMessage({ type: 'need-key' })
        })
    }

    private stopBroadcast(): void {
        this.channel?.close()
        this.channel = null
    }

    /** Called after any successful unlock to enable cross-tab sharing. */
    private onUnlockSuccess(rawKey?: Uint8Array): void {
        if (rawKey) {
            this.cachedRawKey = new Uint8Array(rawKey)
            this.cacheSessionKey(rawKey)
        }
        this.startBroadcastProvider()
    }

    /**
     * Ensure vault backup exists on server (§4.9 push-trigger).
     * Fire-and-forget — never blocks the unlock path.
     * Reads vault record to get credentialId; skips if PIN-only vault.
     */
    private ensureVaultBackupPushed(): void {
        if (!this.backupManager) return
        console.log(`[VaultBackup] ensureVaultBackupPushed triggered`)
        void (async () => {
            try {
                const record = await this.store.read()
                if (!record?.credentialId) return // PIN-only vault — no backup possible
                await this.backupManager!.pushBackup(
                    new Uint8Array(record.credentialId),
                    serializeIdentityVaultRecord(record),
                )
            } catch (e) {
                console.warn('Background vault backup re-push failed:', e)
            }
        })()
    }

    private onLockOrDelete(): void {
        if (this.cachedRawKey) { zeroize(this.cachedRawKey); this.cachedRawKey = null }
        if (this.serverGrantSecret) { zeroize(this.serverGrantSecret); this.serverGrantSecret = null }
        this.stopBroadcast()
    }

    isUnlocked(): boolean { return this.unlocked }
    hasIdentity(): boolean { return this.identity !== null }
    getIdentity(): UserIdentity | null { return this.identity }
    get vaultExists(): boolean { return this.vaultExistsCache }
    get vaultUnlockMethod(): VaultUnlockMethod | null { return this.vaultUnlockMethodCache }

    private setCachedVaultState(record: IdentityVaultRecord | null): void {
        this.vaultExistsCache = record !== null
        this.vaultUnlockMethodCache = record?.unlockMethod ?? null
    }

    private overrideCachedVaultState(record: IdentityVaultRecord | null): void {
        this.vaultStateRefreshVersion += 1
        this.setCachedVaultState(record)
    }

    private async refreshCachedVaultState(): Promise<void> {
        const refreshVersion = ++this.vaultStateRefreshVersion
        try {
            const record = await this.store.read()
            if (refreshVersion !== this.vaultStateRefreshVersion) return
            this.setCachedVaultState(record)
        } catch {
            if (refreshVersion !== this.vaultStateRefreshVersion) return
            this.setCachedVaultState(null)
        }
    }

    async unlock(pin?: string): Promise<void> {
        const record = await this.store.read()
        this.overrideCachedVaultState(record)
        if (!record) throw new Error('No identity in vault')

        const usePin = pin ?? this.pinInMemory
        let payload: StoredIdentityPayload | null

        if (record.unlockMethod === 'webauthn' && !usePin) {
            const webAuthn = this.opts.webAuthn ?? new BrowserWebAuthnAdapter()
            const credentialId = new Uint8Array(record.credentialId!)
            const assertion = await webAuthn.getAssertion(credentialId)
            if (!assertion.ok) throw new Error('WebAuthn assertion failed')
            payload = await loadIdentityFromVault(this.store, credentialId)
        } else {
            if (!usePin) throw new Error('PIN required for unlock')
            payload = await loadIdentityFromVault(this.store, usePin)
        }

        if (!payload) throw new Error('Failed to load identity from vault')
        this.identity = toCompatIdentity(payload)
        this.unlocked = true
        this.pinInMemory = usePin ?? null

        // §4.9: Centralized vault backup push (covers all unlock methods)
        this.ensureVaultBackupPushed()

        this.armAutoLock()
    }

    async recoverFromServer(): Promise<boolean> {
        if (!this.backupManager) return false
        const webAuthn = this.opts.webAuthn ?? new BrowserWebAuthnAdapter()

        // Use discoverable credential to find the account
        const assertion = await webAuthn.getDiscoverableAssertion()
        if (!assertion) return false

        const backup = await this.backupManager.pullBackup(assertion.credentialId)
        if (!backup) return false

        let rawKey: Uint8Array
        let payload: StoredIdentityPayload | null

        if (isSerializedIdentityVaultRecord(backup)) {
            // Restore the exact encrypted vault record instead of rewrapping into a fallback mode.
            const record = deserializeIdentityVaultRecord(backup)
            await this.store.write(record)
            this.overrideCachedVaultState(record)

            if (record.unlockMethod === 'webauthn-prf' && record.credentialId && record.prfSalt) {
                const unlockAssertion = await webAuthn.getAssertion(
                    new Uint8Array(record.credentialId),
                    undefined,
                    new Uint8Array(record.prfSalt),
                )
                if (!unlockAssertion.ok || !unlockAssertion.prfOutput) return false
                const recovered = await loadIdentityWithPrf(this.store, unlockAssertion.prfOutput)
                if (!recovered) return false
                rawKey = recovered.rawKey
                payload = recovered.payload
            } else if (record.credentialId) {
                const credentialId = new Uint8Array(record.credentialId)
                const wrapKey = await deriveWebAuthnWrapKey(credentialId, new Uint8Array(record.keyWrapSalt))
                const decrypted = await decryptAesGcm(wrapKey, new Uint8Array(record.keyWrapIv), record.vaultKeyCiphertext)
                rawKey = new Uint8Array(decrypted)
                payload = await loadIdentityWithRawKey(this.store, rawKey)
            } else {
                return false
            }
        } else {
            // Compatibility path for older backups that stored the decrypted payload.
            rawKey = await saveIdentityToVault(this.store, backup as StoredIdentityPayload, {
                credentialId: assertion.credentialId,
                prfOutput: assertion.prfOutput,
            })
            await this.refreshCachedVaultState()
            payload = backup as StoredIdentityPayload
        }
        if (!payload) return false

        this.onUnlockSuccess(rawKey)
        zeroize(rawKey)
        this.identity = toCompatIdentity(payload)
        this.unlocked = true
        this.pinInMemory = null
        this.armAutoLock()
        return true
    }

    lock(): void {
        this.unlocked = false; this.pinInMemory = null; this.identity = null
        this.clearSessionKeyCache()
        this.onLockOrDelete()
        if (this.lockTimer) { clearTimeout(this.lockTimer); this.lockTimer = null }
    }

    async createIdentity(params?: { name?: string; emoji?: string; pin?: string; webAuthnOnly?: boolean }): Promise<UserIdentity> {
        const canonical = await createCanonicalIdentity()
        const stored: StoredIdentityPayload = {
            canonical,
            profile: { name: params?.name ?? generateRandomUsername(), emoji: params?.emoji ?? '🔑' },
        }

        const webAuthn = this.opts.webAuthn ?? new BrowserWebAuthnAdapter()
        const result = await webAuthn.createCredential(stored.profile?.name)

        let rawKey: Uint8Array
        if (result) {
            rawKey = await saveIdentityToVault(this.store, stored, {
                credentialId: result.credentialId,
                prfOutput: result.prfOutput,
                prfSalt: result.prfSalt,
            })
            this.pinInMemory = null
        } else {
            // Enhanced enforcement (v0.1): If webAuthnOnly is requested (onboarding flow),
            // do not allow silent fallback to a default PIN.
            if (params?.webAuthnOnly) {
                throw new Error('Identity creation requires WebAuthn/Passkey registration.')
            }

            const pin = params?.pin ?? '0000'
            rawKey = await saveIdentityToVault(this.store, stored, { pin })
            this.pinInMemory = pin
        }
        await this.refreshCachedVaultState()

        this.onUnlockSuccess(rawKey)
        zeroize(rawKey)
        this.identity = toCompatIdentity(stored)
        this.unlocked = true

        if (this.backupManager && result) {
            const record = await this.store.read()
            if (record) {
                await this.backupManager.pushBackup(
                    result.credentialId,
                    serializeIdentityVaultRecord(record),
                ).catch(e => console.warn('Vault backup failed:', e))
            }
        }

        this.armAutoLock()
        return this.identity
    }

    async importIdentity(payload: string): Promise<UserIdentity> {
        const parsed = this.parseExportPayload(payload)
        const stored = await fromCompatIdentity(parsed)

        const webAuthn = this.opts.webAuthn ?? new BrowserWebAuthnAdapter()
        const result = await webAuthn.createCredential(parsed.name)
        if (!result) {
            throw new Error('Identity import requires WebAuthn/Passkey registration.')
        }
        const rawKey = await saveIdentityToVault(this.store, stored, {
            credentialId: result.credentialId,
            prfOutput: result.prfOutput,
            prfSalt: result.prfSalt,
        })
        await this.refreshCachedVaultState()
        this.onUnlockSuccess(rawKey)
        zeroize(rawKey)
        this.pinInMemory = null
        this.identity = toCompatIdentity(stored)
        this.unlocked = true

        if (this.backupManager) {
            const record = await this.store.read()
            if (record) {
                await this.backupManager.pushBackup(
                    result.credentialId,
                    serializeIdentityVaultRecord(record),
                ).catch(e => console.warn('Vault backup failed during import:', e))
            }
        }

        this.armAutoLock()
        return this.identity
    }

    exportIdentity(): string {
        if (!this.identity) return ''
        const payload = encodeBase64Url(new TextEncoder().encode(JSON.stringify(this.identity)))
        return `sb-key:${payload}`
    }

    async updateProfile(profile: { name?: string; emoji?: string }): Promise<void> {
        if (!this.unlocked || !this.identity) throw new Error('Vault must be unlocked to update profile')

        // Re-read the current stored payload to update it
        const record = await this.store.read()
        if (!record) throw new Error('No vault record found')

        let storedPayload: StoredIdentityPayload | null
        if (this.cachedRawKey) {
            // Preserve the existing wrap method (pin, webauthn, or webauthn-prf)
            // when the vault is already open in memory.
            storedPayload = await loadIdentityWithRawKey(this.store, this.cachedRawKey)
        } else if (record.unlockMethod === 'webauthn' && record.credentialId) {
            storedPayload = await loadIdentityFromVault(this.store, new Uint8Array(record.credentialId))
        } else if (this.pinInMemory) {
            storedPayload = await loadIdentityFromVault(this.store, this.pinInMemory)
        } else {
            throw new Error('Cannot update profile: no credentials available for re-encryption')
        }
        if (!storedPayload) throw new Error('Failed to load vault for profile update')

        // Merge profile changes
        storedPayload.profile = {
            ...storedPayload.profile,
            ...profile,
        }

        // Re-save with updated profile
        if (this.cachedRawKey) {
            await saveIdentityWithRawKey(this.store, storedPayload, this.cachedRawKey)
        } else if (record.unlockMethod === 'webauthn' && record.credentialId) {
            await saveIdentityToVault(this.store, storedPayload, { credentialId: new Uint8Array(record.credentialId) })
        } else if (this.pinInMemory) {
            await saveIdentityToVault(this.store, storedPayload, { pin: this.pinInMemory })
        }
        await this.refreshCachedVaultState()

        // Update in-memory identity
        this.identity = toCompatIdentity(storedPayload)

        // Sync updated profile to backup
        if (this.backupManager && record.credentialId) {
            const updatedRecord = await this.store.read()
            if (updatedRecord) {
                await this.backupManager.pushBackup(
                    new Uint8Array(updatedRecord.credentialId!),
                    serializeIdentityVaultRecord(updatedRecord),
                ).catch(e => console.warn('Profile update backup failed:', e))
            }
        }
    }

    private parseExportPayload(payload: string): UserIdentity {
        // Only accept sb-key: prefix
        const encoded = payload.startsWith('sb-key:') ? payload.slice('sb-key:'.length) : payload
        let parsed: UserIdentity
        try {
            parsed = JSON.parse(new TextDecoder().decode(decodeBase64Url(encoded))) as UserIdentity
        } catch {
            parsed = JSON.parse(encoded) as UserIdentity
        }
        if (parsed.canonical) validateCanonicalIdentity(parsed.canonical)
        return parsed
    }

    private armAutoLock(): void {
        if (this.lockTimer) clearTimeout(this.lockTimer)
        this.lockTimer = setTimeout(() => {
            this.lock()
            this.opts.onAutoLock?.()
        }, this.opts.autoLockMs ?? AUTO_LOCK_DEFAULT_MS)
    }

    private async buildTrustedDeviceManagementProof(
        operation: 'register' | 'list' | 'revoke' | 'revoke-all',
        deviceId?: string,
    ): Promise<{ primaryKid: string; primaryPk: string; challenge: string; signature: string }> {
        if (!this.identity?.canonical) throw new Error('Canonical identity required for trusted-device management')

        const primary = this.identity.canonical.keyRing.find((entry) => entry.source === 'primary')
        if (!primary) throw new Error('No primary key found')

        const challengeRes = await fetch(`${this.trustedDeviceApiBase}/challenge`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                operation,
                primaryKid: primary.kid,
                primaryPk: primary.pk,
                deviceId,
            }),
        })
        if (!challengeRes.ok) throw new Error(`Challenge request failed: ${challengeRes.status}`)
        const { challenge } = await challengeRes.json() as { challenge: string }

        const primarySeedBytes = decodeBase64Url(this.identity.canonical.primarySeed)
        try {
            const { signAsync } = await import('@noble/ed25519')
            const challengeHash = sha256(new TextEncoder().encode(challenge))
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore noble typings lag Uint8Array generics
            const sig = await signAsync(challengeHash, primarySeedBytes)
            return {
                primaryKid: primary.kid,
                primaryPk: primary.pk,
                challenge,
                signature: encodeBase64Url(sig),
            }
        } finally {
            zeroize(primarySeedBytes)
        }
    }

    // ─── Trusted Device methods (§05 §10) ─────────────────────────────────

    /**
     * Register this device as a trusted device for the current identity.
     * Flow (§05 §10.4):
     * 1. Generate device keys + local secret
     * 2. Get server challenge (identity-signed)
     * 3. Sign challenge with primaryKid
     * 4. POST /register with device info + signature
     * 5. Server returns serverGrantSecret — store locally + wrap K_vault
     *
     * Requires the vault to be currently unlocked.
     */
    async trustDevice(label?: string): Promise<{ deviceId: string }> {
        if (!this.unlocked || !this.identity) throw new Error('Vault must be unlocked to trust this device')

        // 1. Generate device auth key pair and local secret
        const deviceId = crypto.randomUUID()
        const { privateKey, publicKeyBase64 } = await generateDeviceAuthKeyPair()
        const deviceLocalSecret = crypto.getRandomValues(new Uint8Array(32))
        const managementProof = await this.buildTrustedDeviceManagementProof('register', deviceId)

        // 4. Register with server
        // First we need to get the raw vault key to wrap
        const record = await this.store.read()
        if (!record) throw new Error('No vault record')

        // Derive the current K_vault from the existing vault record
        let rawVaultKey: Uint8Array
        if (record.unlockMethod === 'webauthn-prf') {
            // PRF vault: key was derived from PRF output and cached on unlock — use cached key
            rawVaultKey = this.cachedRawKey ? new Uint8Array(this.cachedRawKey) : (() => { throw new Error('Vault must be unlocked with WebAuthn to enroll trusted device') })()
        } else if (record.unlockMethod === 'webauthn') {
            // We need the credential to derive the key — the vault is already unlocked so we use the cached raw key
            rawVaultKey = this.cachedRawKey ? new Uint8Array(this.cachedRawKey) : (() => { throw new Error('Vault must be unlocked with WebAuthn to enroll trusted device') })()
        } else {
            throw new Error('Trusted device enrollment requires WebAuthn vault')
        }

        // 5. POST register — server will respond with serverGrantSecret
        const registerRes = await fetch(`${this.trustedDeviceApiBase}/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                deviceId,
                devicePublicKey: publicKeyBase64,
                primaryKid: managementProof.primaryKid,
                primaryPk: managementProof.primaryPk,
                label,
                challenge: managementProof.challenge,
                signature: managementProof.signature,
            }),
        })
        if (!registerRes.ok) throw new Error(`Device registration failed: ${registerRes.status}`)
        const {
            serverGrantSecret: serverGrantSecretB64,
            envelopeUploadToken,
        } = await registerRes.json() as { serverGrantSecret: string; envelopeUploadToken: string }
        const serverGrantSecret = decodeBase64Url(serverGrantSecretB64)

        // 6. Wrap K_vault with the derived trusted device wrap key
        const wrapped = await wrapVaultKeyWithTrustedDeviceKey(rawVaultKey, deviceLocalSecret, serverGrantSecret, managementProof.primaryKid)

        // 7. Submit the envelope to the server
        const envelopeRes = await fetch(`${this.trustedDeviceApiBase}/envelope`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                deviceId,
                envelopeUploadToken,
                vaultEnvelopeCiphertext: encodeBase64Url(new Uint8Array(wrapped.vaultEnvelopeCiphertext)),
                keyWrapSalt: encodeBase64Url(new Uint8Array(wrapped.keyWrapSalt)),
                keyWrapIv: encodeBase64Url(new Uint8Array(wrapped.keyWrapIv)),
            }),
        })
        if (!envelopeRes.ok) throw new Error(`Envelope submission failed: ${envelopeRes.status}`)

        // 8. Store local record
        const localRecord: TrustedDeviceLocalRecord = {
            deviceId,
            deviceAuthPrivateKey: privateKey,
            deviceLocalSecret,
            primaryKid: managementProof.primaryKid,
            createdAt: new Date().toISOString(),
        }
        await this.trustedDeviceStore.write(localRecord)

        // Store serverGrantSecret in memory for session
        this.serverGrantSecret = serverGrantSecret

        return { deviceId }
    }

    /**
     * List all trusted devices for the current identity (from server).
     * Requires the vault to be unlocked.
     */
    async getTrustedDevices(): Promise<TrustedDeviceInfo[]> {
        if (!this.unlocked || !this.identity) throw new Error('Vault must be unlocked')

        const proof = await this.buildTrustedDeviceManagementProof('list')
        const res = await fetch(`${this.trustedDeviceApiBase}/list`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                primaryKid: proof.primaryKid,
                primaryPk: proof.primaryPk,
                challenge: proof.challenge,
                signature: proof.signature,
            }),
        })
        if (!res.ok) throw new Error(`List devices failed: ${res.status}`)
        const devices = await res.json() as TrustedDeviceInfo[]
        return devices
    }

    /**
     * Revoke a specific trusted device.
     * Requires the vault to be unlocked.
     */
    async revokeTrustedDevice(deviceId: string): Promise<void> {
        if (!this.unlocked || !this.identity) throw new Error('Vault must be unlocked')

        const proof = await this.buildTrustedDeviceManagementProof('revoke', deviceId)
        const res = await fetch(`${this.trustedDeviceApiBase}/${deviceId}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                primaryKid: proof.primaryKid,
                primaryPk: proof.primaryPk,
                challenge: proof.challenge,
                signature: proof.signature,
            }),
        })
        if (!res.ok) throw new Error(`Revoke device failed: ${res.status}`)

        // If revoking the current device, clear local record
        const localRecord = await this.trustedDeviceStore.read()
        if (localRecord?.deviceId === deviceId) {
            await this.trustedDeviceStore.clear()
            if (this.serverGrantSecret) { zeroize(this.serverGrantSecret); this.serverGrantSecret = null }
        }
    }

    /**
     * Revoke ALL trusted devices for the current identity.
     * Requires the vault to be unlocked.
     */
    async revokeAllTrustedDevices(): Promise<void> {
        if (!this.unlocked || !this.identity) throw new Error('Vault must be unlocked')

        const proof = await this.buildTrustedDeviceManagementProof('revoke-all')
        const res = await fetch(`${this.trustedDeviceApiBase}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                primaryKid: proof.primaryKid,
                primaryPk: proof.primaryPk,
                challenge: proof.challenge,
                signature: proof.signature,
            }),
        })
        if (!res.ok) throw new Error(`Revoke all devices failed: ${res.status}`)

        // Clear local state
        await this.trustedDeviceStore.clear()
        if (this.serverGrantSecret) { zeroize(this.serverGrantSecret); this.serverGrantSecret = null }
    }

    /**
     * Check if the current device is enrolled as a trusted device.
     */
    async isCurrentDeviceTrusted(): Promise<boolean> {
        const record = await this.trustedDeviceStore.read()
        return record !== null
    }

    /**
     * Attempt trusted-device unlock using the stored local record + server grant.
     * Called internally by tryAutoUnlock() Layer 3.
     */
    private async tryTrustedDeviceUnlock(): Promise<boolean> {
        if (!this.serverGrantSecret) return false

        const localRecord = await this.trustedDeviceStore.read()
        if (!localRecord) return false

        const primaryKid = localRecord.primaryKid

        // Get unlock challenge from server (device-signed)
        let challenge: string
        try {
            const challengeRes = await fetch(`${this.trustedDeviceApiBase}/unlock-challenge`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ deviceId: localRecord.deviceId }),
            })
            if (challengeRes.status === 403) {
                // Device revoked — clear local state
                await this.trustedDeviceStore.clear()
                return false
            }
            if (!challengeRes.ok) return false
            const data = await challengeRes.json() as { challenge: string }
            challenge = data.challenge
        } catch {
            // Network error — fail silently, let caller handle fallback
            return false
        }

        // Sign challenge with device auth key
        const signature = await signWithDeviceKey(localRecord.deviceAuthPrivateKey, challenge)

        // Submit unlock — server verifies device signature + checks not revoked
        // Server responds with { serverGrantSecret } if valid
        const unlockRes = await fetch(`${this.trustedDeviceApiBase}/unlock`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                deviceId: localRecord.deviceId,
                signature,
                challenge,
            }),
        })
        if (unlockRes.status === 403) {
            // Revoked
            await this.trustedDeviceStore.clear()
            return false
        }
        if (!unlockRes.ok) return false

        // Server returned a fresh serverGrantSecret plus the wrapped envelope.
        const { serverGrantSecret: freshGrantB64, envelope: res } = await unlockRes.json() as {
            serverGrantSecret: string
            envelope: TrustedDeviceEnvelopeResponse
        }
        if (this.serverGrantSecret) zeroize(this.serverGrantSecret)
        this.serverGrantSecret = decodeBase64Url(freshGrantB64)
        if (res.revokedAt) {
            await this.trustedDeviceStore.clear()
            return false
        }

        // Decode base64url fields to ArrayBuffers
        const envelope: TrustedDeviceEnvelope = {
            deviceId: res.deviceId,
            vaultEnvelopeCiphertext: decodeBase64Url(res.vaultEnvelopeCiphertext).buffer as ArrayBuffer,
            keyWrapSalt: decodeBase64Url(res.keyWrapSalt).buffer as ArrayBuffer,
            keyWrapIv: decodeBase64Url(res.keyWrapIv).buffer as ArrayBuffer,
            devicePublicKey: res.devicePublicKey,
            label: res.label,
            createdAt: res.createdAt,
            lastSeenAt: res.lastSeenAt,
            revokedAt: res.revokedAt,
        }

        // Unwrap K_vault
        let rawVaultKey: Uint8Array
        try {
            rawVaultKey = await unwrapVaultKeyWithTrustedDeviceKey(
                envelope,
                localRecord.deviceLocalSecret,
                this.serverGrantSecret,
                primaryKid,
            )
        } catch {
            return false
        }

        // Load identity with the raw vault key
        const payload = await loadIdentityWithRawKey(this.store, rawVaultKey)
        if (!payload) { zeroize(rawVaultKey); return false }

        this.identity = toCompatIdentity(payload)
        this.unlocked = true
        this.cachedRawKey = new Uint8Array(rawVaultKey)
        this.cacheSessionKey(rawVaultKey)
        zeroize(rawVaultKey)

        this.ensureVaultBackupPushed()
        this.armAutoLock()
        this.startBroadcastProvider()
        return true
    }

    /**
     * Obtain a serverGrantSecret from the server for the enrolled local device.
     * Called when we have a local record but no in-memory serverGrantSecret.
     */
    private async obtainServerGrantSecret(localRecord: TrustedDeviceLocalRecord): Promise<boolean> {
        try {
            // Get challenge
            const challengeRes = await fetch(`${this.trustedDeviceApiBase}/unlock-challenge`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ deviceId: localRecord.deviceId }),
            })
            if (challengeRes.status === 403) {
                await this.trustedDeviceStore.clear()
                return false
            }
            if (!challengeRes.ok) return false
            const { challenge } = await challengeRes.json() as { challenge: string }

            // Sign with device key
            const signature = await signWithDeviceKey(localRecord.deviceAuthPrivateKey, challenge)

            // Get grant
            const grantRes = await fetch(`${this.trustedDeviceApiBase}/grant`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    deviceId: localRecord.deviceId,
                    signature,
                    challenge,
                }),
            })
            if (grantRes.status === 403) {
                await this.trustedDeviceStore.clear()
                return false
            }
            if (!grantRes.ok) return false

            const { serverGrantSecret: grantB64 } = await grantRes.json() as { serverGrantSecret: string }
            if (this.serverGrantSecret) zeroize(this.serverGrantSecret)
            this.serverGrantSecret = decodeBase64Url(grantB64)
            return true
        } catch {
            return false
        }
    }

    /**
     * Attempt to auto-unlock the vault **without blocking the UI**.
     *
     * Unlock cascade (§4.6 + §05 §10.5):
     * 1. Session key cache (sessionStorage) — works for ALL vault types
     * 2. Cross-tab key sharing (BroadcastChannel)
     * 3. Trusted-device online silent unlock — requires online + serverGrantSecret
     * 4. Fall through → return false (caller shows UnlockPrompt)
     *
     * WebAuthn assertion is NEVER attempted here (requires user gesture).
     */
    async tryAutoUnlock(): Promise<boolean> {
        if (this.unlocked && this.identity) return true

        try {
            const record = await this.store.read()
            this.overrideCachedVaultState(record)
            if (!record) return false

            // Layer 1: Session key cache (survives F5/reload within same browser session)
            const cachedKey = this.getCachedSessionKey()
            if (cachedKey) {
                try {
                    const payload = await loadIdentityWithRawKey(this.store, cachedKey)
                    if (payload) {
                        this.identity = toCompatIdentity(payload)
                        this.unlocked = true
                        this.onUnlockSuccess(cachedKey)
                        this.ensureVaultBackupPushed()
                        this.armAutoLock()
                        return true
                    }
                } catch {
                    // Cached key invalid (vault re-encrypted, etc.) — clear and fall through
                    this.clearSessionKeyCache()
                }
            }

            // Layer 2: Cross-tab key sharing (BroadcastChannel)
            const crossTabKey = await this.requestKeyFromOtherTab()
            if (crossTabKey) {
                try {
                    const payload = await loadIdentityWithRawKey(this.store, crossTabKey)
                    if (payload) {
                        this.identity = toCompatIdentity(payload)
                        this.unlocked = true
                        this.onUnlockSuccess(crossTabKey)
                        this.ensureVaultBackupPushed()
                        this.armAutoLock()
                        return true
                    }
                } catch {
                    /* key from other tab was invalid — fall through */
                }
            }

            // Layer 3: Trusted-device online silent unlock (§05 §10.5)
            {
                const localRecord = await this.trustedDeviceStore.read()
                if (localRecord) {
                    if (!this.serverGrantSecret) {
                        // No in-memory grant — try to obtain one from server
                        const obtained = await this.obtainServerGrantSecret(localRecord)
                        if (!obtained) {
                            // Server unreachable or revoked — fall through
                        } else {
                            const ok = await this.tryTrustedDeviceUnlock()
                            if (ok) return true
                        }
                    } else {
                        // We already have a serverGrantSecret from a previous unlock
                        const ok = await this.tryTrustedDeviceUnlock()
                        if (ok) return true
                    }
                }
            }

            if (record.unlockMethod === 'webauthn') {
                // Do NOT attempt navigator.credentials.get() here.
                // Safari always shows a full modal for userVerification:'required',
                // and aborting it causes the browser to hang/freeze.
                // The user must unlock explicitly via unlockWithWebAuthn().
                return false
            }

            // Layer 4: PIN cache (in-memory, only survives within same page lifecycle)
            if (this.pinInMemory) {
                const payload = await loadIdentityFromVault(this.store, this.pinInMemory)
                if (!payload) return false

                this.identity = toCompatIdentity(payload)
                this.unlocked = true
                this.armAutoLock()
                return true
            }

            return false
        } catch (e) {
            console.warn('Auto-unlock failed:', e)
            return false
        }
    }

    /**
     * Explicitly unlock a WebAuthn vault via browser assertion prompt.
     * Call this from UI when the user taps "Unlock with Passkey".
     * On success, caches session key so future reloads skip the prompt.
     *
     * Handles both:
     * - 'webauthn-prf': Hardware-bound PRF output → HKDF key unwrap (most secure)
     * - 'webauthn': Legacy PBKDF2(credentialId) fallback
     */
    async unlockWithWebAuthn(): Promise<boolean> {
        if (this.unlocked && this.identity) return true

        try {
            const record = await this.store.read()
            this.overrideCachedVaultState(record)
            if (!record || (record.unlockMethod !== 'webauthn' && record.unlockMethod !== 'webauthn-prf')) return false

            const webAuthn = this.opts.webAuthn ?? new BrowserWebAuthnAdapter()
            const credentialId = new Uint8Array(record.credentialId!)

            // Request PRF output during assertion if this is a PRF-protected vault
            const prfSalt = record.unlockMethod === 'webauthn-prf' && record.prfSalt
                ? new Uint8Array(record.prfSalt)
                : undefined
            const assertion = await webAuthn.getAssertion(credentialId, undefined, prfSalt)
            if (!assertion.ok) return false

            let rawVaultKey: Uint8Array
            let payload: StoredIdentityPayload | null

            if (record.unlockMethod === 'webauthn-prf' && assertion.prfOutput) {
                // PRF path: use hardware-bound output to unwrap vault key
                const result = await loadIdentityWithPrf(this.store, assertion.prfOutput)
                if (!result) return false
                rawVaultKey = result.rawKey
                payload = result.payload
            } else {
                // Legacy path: derive wrap key from credentialId
                const wrapKey = await deriveWebAuthnWrapKey(credentialId, new Uint8Array(record.keyWrapSalt))
                const decrypted = await decryptAesGcm(wrapKey, new Uint8Array(record.keyWrapIv), record.vaultKeyCiphertext)
                rawVaultKey = new Uint8Array(decrypted)
                payload = await loadIdentityWithRawKey(this.store, rawVaultKey)
            }

            // Cache for session persistence before decrypting identity
            this.onUnlockSuccess(rawVaultKey)
            zeroize(rawVaultKey)

            if (!payload) return false

            this.identity = toCompatIdentity(payload)
            this.unlocked = true
            this.pinInMemory = null
            this.ensureVaultBackupPushed()
            this.armAutoLock()
            return true
        } catch (e) {
            console.warn('WebAuthn unlock failed:', e)
            return false
        }
    }

    /** Permanently delete vault record and lock. Irreversible. */
    async deleteVault(): Promise<void> {
        // Best-effort: revoke trusted devices on server while identity is still available
        if (this.unlocked && this.identity) {
            await this.revokeAllTrustedDevices().catch(() => { /* best effort — don't block vault deletion on network errors */ })
        }
        // Always clear local trusted device state even if server revocation failed
        await this.trustedDeviceStore.clear()

        this.clearSessionKeyCache()
        this.onLockOrDelete()
        this.lock()
        await this.store.clear()
        this.overrideCachedVaultState(null)
    }

    /** Read the unlock method without unlocking. Returns null if no vault. */
    async getVaultUnlockMethod(): Promise<VaultUnlockMethod | null> {
        await this.refreshCachedVaultState()
        return this.vaultUnlockMethod
    }

    /** Check if a vault record exists in storage (without unlocking). */
    async hasVaultRecord(): Promise<boolean> {
        await this.refreshCachedVaultState()
        return this.vaultExists
    }
}
